import { lazy, ComponentType } from 'react';

/**
 * A wrapper around React.lazy that retries the import if it fails.
 * This is particularly useful for handling ChunkLoadError which often
 * happens after a new deployment or due to network issues.
 */
export const lazyWithRetry = (
    componentImport: () => Promise<{ default: ComponentType<any> }>,
    retriesLeft = 5,
    interval = 1000
): any => {
    return lazy(async () => {
        try {
            return await componentImport();
        } catch (error: any) {
            if (retriesLeft <= 0) {
                throw error;
            }

            // Retry after interval
            await new Promise((resolve) => setTimeout(resolve, interval));

            // If we reach here, we'll try again with one less retry
            // Note: we need to cast back to the same return type
            return (lazyWithRetry(componentImport, retriesLeft - 1, interval) as any)._payload._result();
        }
    });
};

/**
 * Alternative version that doesn't use recursion inside lazy
 */
export const lazyRetry = (componentImport: () => Promise<{ default: ComponentType<any> }>, name?: string) =>
    lazy(async () => {
        const pageHasAlreadyBeenForceRefreshed = JSON.parse(
            window.localStorage.getItem('page-has-been-force-refreshed') || 'false'
        );

        try {
            const component = await componentImport();
            if (pageHasAlreadyBeenForceRefreshed) {
                window.localStorage.setItem('page-has-been-force-refreshed', 'false');
            }
            return component;
        } catch (error) {
            console.error(`[lazyRetry] erro ao carregar chunk ${name || ''}:`, error);

            if (!pageHasAlreadyBeenForceRefreshed) {
                console.warn(`[lazyRetry] falha detectada. recarregando a página para tentar novamente...`);
                window.localStorage.setItem('page-has-been-force-refreshed', 'true');
                window.location.reload();
                return { default: () => null } as any;
            }

            console.error(`[lazyRetry] falha persistente após recarregamento.`);
            throw error;
        }
    });
